import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import registration from '@/helper/shemaValidation/registration';
import { formaterLogin, redirect, validField } from '@/helper/functions';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { setFormRegistration, setLoad } from '@/redux/formsReducer';

import Field from '@/components/field';
import ErrorComponent from '@/components/UI/errorField';
import { IField } from '@/types/forms';
import Button from '@/components/UI/button';
import ErrorForm from '@/components/UI/errorForm';
import LoaderIcon from '@/components/UI/loader';

import style from './customForm.module.scss';
import { get, post } from '@/helper/axiosRequest';
import { setError } from '@/redux/rootReducer';
import { useTranslation } from 'react-i18next';

type Props = {
  initialValues: any;
  fields: IField[];
  reload: () => void;
};

const FormRegistration: React.FC<Props> = ({
  initialValues,
  fields = [],
  reload,
}) => {
  const [busyLogin, setBusyLogin] = useState(false);
  const [busyEmail, setBusyEmail] = useState(false);
  const [searchLogin, setSearchLogin] = useState(false);
  const [searchEmail, setSearchEmail] = useState(false);

  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const error = useAppSelector((state) => state.rootState.error);
  const registrationRestoran = useAppSelector(
    (state) => state.formState.formRegistrationRestoran
  );
  const loader = useAppSelector((state) => state.rootState.loader.registration);
  const router = useRouter();
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: registration,
    validateOnChange: false,
    onSubmit: (values) => {
      dispatch(
        setFormRegistration({
          ...values,
          login: formaterLogin(values.login),
          confirmPassword: '',
        })
      );
      sessionStorage.setItem(
        'registration',
        JSON.stringify({
          ...values,
          login: formaterLogin(values.login),
          password: '',
          confirmPassword: '',
        })
      );

      if (Object.values(registrationRestoran).every((el) => el.length)) {
        const registrationError = sessionStorage.getItem('errorTenant');

        if (registrationError) {
          sessionStorage.removeItem('errorTenant');
        }
      }

      dispatch(setLoad(true));
      redirect(router, 'registrationRestoran');
    },
  });

  useEffect(() => {
    const errorTenant = sessionStorage.getItem('errorTenant');
    const payID = sessionStorage.getItem('pay');

    if (payID) {
      sessionStorage.removeItem('pay');
    }

    if (errorTenant) {
      formik.setFieldError(
        errorTenant.includes('slug') ? 'login' : 'email',
        JSON.parse(errorTenant)
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadForm = (e: any) => {
    e.preventDefault();
    reload();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validLogin = async () => {
    const fieldProps = formik.getFieldProps('login');
    if (fieldProps.value) {
      setSearchLogin(true);
      try {
        const freeDomain = await get<string>(
          `subDomain?domain=${fieldProps.value}`,
          'busy'
        );
        if (JSON.parse(freeDomain)) {
          validField(
            '',
            'login',
            formik.setFieldError,
            'formRegistration.loginBusyError'
          );
          setSearchLogin(false);
          setBusyLogin(true);
        } else {
          validField(
            'fieldProps.value',
            'login',
            formik.setFieldError,
            'formRegistration.loginBusyError'
          );
          setBusyLogin(false);
          setSearchLogin(false);
        }
      } catch (error) {
        dispatch(setError(true));
      }
    }
  };

  const validEmail = async () => {
    const fieldProps = formik.getFieldProps('email');
    try {
      fieldProps.value && setSearchEmail(true);
      if (fieldProps.value) {
        const response = await post<any>(
          'checkEmail',
          { email: fieldProps.value },
          i18n.language
        );
        setBusyEmail(response.exist);

        if (response.exist) {
          validField(
            '',
            'email',
            formik.setFieldError,
            'formRegistration.emailBusy'
          );
          setSearchEmail(false);
        }

        setSearchEmail(false);
      }
    } catch (error) {
      setSearchEmail(false);
    }
  };

  const renderErrors = useMemo(() => {
    if (formik && formik.errors) {
      const errorArray = Object.values(formik.errors);
      return errorArray.map((error, index) => (
        <ErrorComponent key={index} error={error as string} />
      ));
    }
  }, [formik]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateField = (value: string, name: string, errorText: string) => {
    validField(value, name, formik.setFieldError, errorText);
    name === 'login' && validLogin();
    name === 'email' && validEmail();
  };

  const renderFields = () =>
    !error && (
      <div className={style.form_wrapper}>
        {fields.map((field, index) => {
          return (
            <Field
              id={field.name}
              placeholder={field.placeholder}
              label={field.label}
              type={field.type}
              name={field.name}
              onChange={formik.handleChange}
              onBlur={() =>
                validateField(
                  formik.values[field.name],
                  field.name,
                  field.errorText
                )
              }
              value={formik.values[field.name]}
              error={formik.errors[field.name]}
              key={index}
              loader={
                (field.name === 'login' && searchLogin) ||
                (field.name === 'email' && searchEmail)
              }
            />
          );
        })}
      </div>
    );

  const renderError = () => error && <ErrorForm />;

  return (
    <form
      onSubmit={error ? reloadForm : formik.handleSubmit}
      className={classNames(style.form, {
        [style.loader]: loader,
      })}
    >
      {loader ? (
        <LoaderIcon />
      ) : (
        <>
          {renderFields()}
          {renderError()}
          <div className={style.wrapper_errors}>{renderErrors}</div>
          <div className={style.form_button_wrapper}>
            <Button
              text={
                error
                  ? 'formCustomizationRestoran.buttonReload'
                  : 'formCustomizationRestoran.buttonNext'
              }
              otherStyle={classNames(style.form_button, {
                [style.disable]:
                  busyLogin || searchLogin || searchEmail || busyEmail,
              })}
              disable={busyLogin || searchLogin || searchEmail || busyEmail}
              type='submit'
            ></Button>
          </div>
        </>
      )}
    </form>
  );
};

export default FormRegistration;
